import { useState } from 'react';
import { ChevronDown, X, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';

import type { Advertiser } from './LoadAdvertisersButton';

interface Props {
  /** Internal field name; emitted as a data attribute for integration/debugging. */
  fieldName: string;
  /** Advertisers returned by the companion loader button. Empty -> empty-state. */
  options: Advertiser[];
  /** Currently-selected advertiser ids. */
  value: number[];
  /** Fired with the next number[] (always in server/options order) on change. */
  onChange: (next: number[]) => void;
  label?: string;
  dirty?: boolean;
  disabled?: boolean;
  help_text?: string;
}

const isInactive = (o: Advertiser) => (o.status ?? '').toUpperCase() === 'INACTIVE';

export function ChipMultiSelectDynamic({
  fieldName, options, value, onChange, label, dirty, disabled, help_text,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const valueSet = new Set(value);

  const labelEl = label ? (
    <div className="flex items-center gap-2">
      <Label className="text-[13px] font-medium">{label}</Label>
      {dirty && (
        <Badge variant="default" className="h-[18px] bg-[oklch(85%_0.13_75)] text-[oklch(35%_0.13_75)]">
          Edited
        </Badge>
      )}
    </div>
  ) : null;

  // Empty state -- nothing loaded AND nothing selected yet.
  if (options.length === 0 && value.length === 0) {
    return (
      <div className="flex flex-col gap-1.5">
        {labelEl}
        <div
          data-field-name={fieldName}
          className="rounded-md border border-dashed border-muted-foreground/30 bg-muted/30 p-4 text-sm text-muted-foreground"
        >
          Load advertisers above to populate this list.
        </div>
        {help_text && <p className="text-xs text-muted-foreground">{help_text}</p>}
      </div>
    );
  }

  const filtered = options.filter((o) =>
    !query
    || o.name.toLowerCase().includes(query.toLowerCase())
    || String(o.id).includes(query));

  // Always emit ids in the server-returned options order so toggle order is stable.
  const emit = (nextSet: Set<number>) => {
    // Options order for known ids; then append still-selected ids absent from
    // options (saved advertisers not in the current fetch) so synthetic chips
    // are never dropped. (#627)
    const known = options.filter((o) => nextSet.has(o.id)).map((o) => o.id);
    const knownSet = new Set(known);
    const extra = value.filter((id) => nextSet.has(id) && !knownSet.has(id));
    onChange([...known, ...extra]);
  };

  const toggle = (id: number) => {
    const next = new Set(valueSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    emit(next);
  };
  const remove = (id: number) => {
    const next = new Set(valueSet);
    next.delete(id);
    emit(next);
  };
  const selectAllFiltered = () => {
    const next = new Set(valueSet);
    filtered.forEach((o) => next.add(o.id));
    emit(next);
  };
  const selectNoneFiltered = () => {
    const next = new Set(valueSet);
    filtered.forEach((o) => next.delete(o.id));
    emit(next);
  };

  // Every selected id becomes a chip: use the option's name when known, else
  // fall back to a synthetic id-labeled advertiser so saved selections (and
  // refresh-omitted ids) stay visible. (#627)
  const optionById = new Map(options.map((o) => [o.id, o]));
  const selected: Advertiser[] = value.map(
    (id) => optionById.get(id) ?? { id, name: String(id), status: '' },
  );

  return (
    <div className="flex flex-col gap-1.5" data-field-name={fieldName}>
      {labelEl}
      <Popover open={open} onOpenChange={(o) => !disabled && setOpen(o)}>
        <PopoverTrigger asChild>
          <div
            role="combobox"
            aria-expanded={open}
            aria-haspopup="listbox"
            aria-disabled={disabled || undefined}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((o) => !o); }
            }}
            className={[
              'flex min-h-9 w-full items-center gap-1 rounded-md border border-input bg-background px-2 py-1 text-left text-sm cursor-pointer',
              disabled ? 'opacity-50 cursor-not-allowed' : '',
            ].join(' ')}
          >
            {selected.length === 0 && (
              <span className="px-1 text-muted-foreground">Select advertisers…</span>
            )}
            <div className="flex flex-wrap items-center gap-1">
              {selected.map((o) => (
                <span key={o.id}
                  className="inline-flex items-center gap-1 rounded-sm bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                  {o.name}
                  <button type="button" aria-label={`Remove ${o.name}`}
                    onClick={(e) => { e.stopPropagation(); remove(o.id); }}
                    className="inline-flex items-center justify-center rounded text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </span>
              ))}
            </div>
            <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          </div>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] p-1" align="start">
          <input
            autoFocus type="text" value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…"
            className="mb-1 h-8 w-full rounded-sm border border-input bg-background px-2 text-sm"
          />
          <div className="mb-1 flex items-center justify-between px-1">
            <span className="text-xs text-muted-foreground">{value.length} of {Math.max(options.length, value.length)} selected</span>
            <div className="flex items-center gap-2">
              <button type="button" onClick={selectAllFiltered}
                className="text-xs text-primary hover:underline">Select all</button>
              <button type="button" onClick={selectNoneFiltered}
                className="text-xs text-primary hover:underline">Select none</button>
            </div>
          </div>
          <div className="max-h-64 overflow-auto">
            {filtered.length === 0 && (
              <div className="px-2 py-3 text-sm text-muted-foreground">No matches.</div>
            )}
            {filtered.map((o) => {
              const on = valueSet.has(o.id);
              const inactive = isInactive(o);
              return (
                <div key={o.id} onClick={() => toggle(o.id)}
                  className={[
                    'flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm',
                    on ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50',
                    inactive ? 'text-muted-foreground' : '',
                  ].join(' ')}>
                  <span className={[
                    'inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm border',
                    on ? 'border-primary bg-primary text-primary-foreground' : 'border-input bg-background',
                  ].join(' ')}>
                    {on && <Check className="h-2.5 w-2.5" aria-hidden />}
                  </span>
                  <span className="truncate">
                    {o.name} ({o.id})
                    {inactive && <span className="ml-1 text-xs uppercase tracking-wide">— inactive</span>}
                  </span>
                </div>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      {help_text && <p className="text-xs text-muted-foreground">{help_text}</p>}
    </div>
  );
}
